import React, { FC, useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Card, Chip, CircularProgress, Container, CssBaseline, Divider, Stack, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../components/Header/header';
import { useConnection } from '../connection/Application';
import { CbaPaperDetail, canSit, formatDuration, formatRemaining, paperCrumbs, readBody, startLabel } from '../components/Cba/cbaApi';
import AttemptHistory from '../components/Cba/AttemptHistory';

/**
 * What a student sees before pressing start: how long the paper runs, how many
 * questions it holds, when they will be warned, and whether they already have a
 * sitting to come back to.
 */
const CbaPaper: FC = () => {
  const { paperId } = useParams();
  const connection = useConnection();
  const navigate = useNavigate();

  const [paper, setPaper] = useState<CbaPaperDetail>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchPaper = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await connection.get(`cba/papers/${paperId}`);
      setPaper(readBody<CbaPaperDetail>(response));
    } catch (e) {
      setPaper(undefined);
      setError('This exam paper could not be loaded. It may no longer be available.');
    } finally {
      setLoading(false);
    }
  }, [connection, paperId]);

  useEffect(() => {
    fetchPaper();
  }, [fetchPaper]);

  const resumable = !!paper?.inProgressAttempt && !paper.inProgressAttempt.expired;

  // Starting and resuming are the same call, and the exam screen makes it. This
  // page only decides where to send the student.
  const start = () => navigate(`/cba/papers/${paperId}/sit`);

  const crumbs = paperCrumbs(paper, '', `/cba/papers/${paperId}`);

  return (
    <Container sx={{ minHeight: 'calc(100vh - 185px)' }}>
      <CssBaseline />
      <Header x={{ crumbs }} />

      <Box sx={{ pt: { xs: 12, md: 14 }, pb: 6 }}>
        <Button onClick={() => navigate(-1)} sx={{ mb: 1, pl: 0 }}>
          &larr; Back
        </Button>

        {error !== '' && <Alert severity='error'>{error}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          paper && (
            <Card sx={{ p: { xs: 3, md: 4 }, borderRadius: 3, boxShadow: '0 2px 12px #0000001A' }}>
              <Typography variant='h4' sx={{ color: '#C44A1E', fontWeight: 500 }}>
                {paper.title}
              </Typography>
              {paper.description && (
                <Typography variant='body1' sx={{ color: 'text.secondary', mt: 1 }}>
                  {paper.description}
                </Typography>
              )}

              <Stack direction='row' spacing={4} sx={{ mt: 3, flexWrap: 'wrap', rowGap: 2 }}>
                {[
                  { label: 'Questions', value: `${paper.questionCount}` },
                  { label: 'Marks', value: `${paper.totalMarks}` },
                  { label: 'Time allowed', value: formatDuration(paper.durationMinutes) },
                  ...(paper.passMark !== undefined && paper.passMark !== null
                    ? [{ label: 'Pass mark', value: `${paper.passMark}%` }]
                    : []),
                ].map((fact) => (
                  <Box key={fact.label}>
                    <Typography
                      variant='caption'
                      sx={{
                        color: 'text.secondary',
                        textTransform: 'uppercase',
                        letterSpacing: '.08em',
                      }}>
                      {fact.label}
                    </Typography>
                    <Typography variant='h6' sx={{ fontWeight: 600 }}>
                      {fact.value}
                    </Typography>
                  </Box>
                ))}
              </Stack>

              <Divider sx={{ my: 3 }} />

              <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>
                Before you begin
              </Typography>
              <Box component='ul' sx={{ color: 'text.secondary', pl: 3, mt: 1, mb: 0 }}>
                <li>The clock starts as soon as you open the paper and does not pause.</li>
                {paper.warnAtMinutes?.length > 0 && (
                  <li>You will be warned when {paper.warnAtMinutes.join(', ')} minutes remain.</li>
                )}
                <li>
                  Your answers save as you choose them, and you can change them until you submit.
                </li>
                <li>If you close the paper, the clock keeps running. Reopen it to carry on.</li>
                <li>When the time runs out the paper is submitted and marked for you.</li>
              </Box>

              {resumable && paper.inProgressAttempt && (
                <Alert severity='warning' sx={{ mt: 3 }}>
                  You already have this paper open, with{' '}
                  {formatRemaining(paper.inProgressAttempt.remainingSeconds)} left. Opening it again
                  continues the same attempt.
                </Alert>
              )}

              {paper.attemptCount > 0 && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 2 }}>
                    <Typography variant='subtitle1' sx={{ fontWeight: 600 }}>
                      Your previous attempts
                    </Typography>
                    {paper.bestPercentage !== undefined && (
                      <Chip
                        size='small'
                        color='success'
                        label={`Best ${Math.round(paper.bestPercentage)}%`}
                      />
                    )}
                  </Stack>
                  <AttemptHistory paperId={paper.id} />
                </>
              )}

              <Box sx={{ mt: 4 }}>
                <Button size='large' variant='contained' disabled={!canSit(paper)} onClick={start}>
                  {canSit(paper) ? startLabel(paper) : 'This paper is not ready yet'}
                </Button>
              </Box>
            </Card>
          )
        )}
      </Box>
    </Container>
  );
};

export default CbaPaper;
